import { forwardRef, useRef } from "react";
import { color } from "styled-system";
import styled, { css, keyframes } from "styled-components";
import Motion from "rc-motion";
import { presetColors } from "../../utils/style-util";
import { isNumber, isEmptyString, isNullish } from "../../utils/type-util";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const zoomIn = keyframes(["0%{transform:scale(0) translate(50%,-50%);opacity:0;}100%{transform:scale(1) translate(50%,-50%);}"]);
const zoomOut = keyframes(["0%{transform:scale(1) translate(50%,-50%);}100%{transform:scale(0) translate(50%,-50%);opacity:0;}"]);
const processing = keyframes(["0%{transform:scale(0.8);opacity:0.5;}100%{transform:scale(2.4);opacity:0;}"]);
const makeStatusColor = (status, color) => p => p.status === status && css(["background:", ";"], p.theme.colors[color].base);
const StatusDot = styled.span.withConfig({
  displayName: "badge__StatusDot",
  componentId: "sc-aafp7i-0"
})(["width:8px;height:8px;position:relative;top:-1px;display:inline-block;vertical-align:middle;border-radius:50%;", " ", " ", " ", " ", " ", ""], p => p.status === "default" && css(["background-color:", ";"], p => p.theme.colors.gray[4]), p => p.status === "processing" && css(["position:relative;background-color:", ";&::after{position:absolute;top:0;left:0;width:100%;height:100%;border:1px solid ", ";border-radius:50%;animation:", " 1.2s infinite ease-in-out;content:\"\";}"], p => p.theme.colors.info.base, p => p.theme.colors.info.base, processing), makeStatusColor("success", "positive"), makeStatusColor("error", "negative"), makeStatusColor("warning", "warning"), color);
const StatusText = styled.span.withConfig({
  displayName: "badge__StatusText",
  componentId: "sc-aafp7i-1"
})(["margin-left:", "px;color:", ";font-size:inherit;"], p => p.theme.space.sm, p => p.theme.colors.text);
const BadgeStatus = styled.span.withConfig({
  displayName: "badge__BadgeStatus",
  componentId: "sc-aafp7i-2"
})(["position:relative;display:inline-block;vertical-align:baseline;line-height:inherit;"]);
const BadgeCount = styled.sup.withConfig({
  displayName: "badge__BadgeCount",
  componentId: "sc-aafp7i-3"
})(["--badge-height:20px;position:absolute;top:0;right:0;transform:translate(50%,-50%);transform-origin:100% 0;&[data-dot=\"true\"]{width:8px;height:8px;border-radius:100%;box-shadow:0 0 0 1px ", ";background:", ";z-index:auto;}&[data-dot=\"false\"]{padding:0 6px;color:#fff;box-shadow:0 0 0 1px ", ";background:", ";border-radius:calc(var(--badge-height) / 2);min-width:var(--badge-height);height:var(--badge-height);line-height:var(--badge-height);font-family:", ";font-size:", "px;font-weight:normal;white-space:nowrap;text-align:center;z-index:auto;}&[data-wrapper=\"false\"]{transform:none;position:relative;top:auto;display:block;}&[data-size=\"small\"]{--badge-height:15px;padding:0px 4px;font-size:10px;}&.ant-badge-zoom-appear,&.ant-badge-zoom-enter{animation-fill-mode:both;animation:", ";}&.ant-badge-zoom-leave{animation-fill-mode:both;animation:", ";}&.ant-badge-zoom-leave-active{transform:scale(0) translate(50%,-50%);opacity:0;}"], p => p.theme.colors.bg.component, p => p.theme.colors.secondary.base, p => p.theme.colors.bg.component, p => p.theme.colors.secondary.base, p => p.theme.fonts.secondary, p => p.theme.fontSizes.sm, p => css(["", " ", " ", ""], zoomIn, p.theme.animationDurations.slow, p.theme.transitions.easeOutBack), p => css(["", " ", " ", ""], zoomOut, p.theme.animationDurations.slow, p.theme.transitions.easeInBack));
const BadgeRoot = styled.span.withConfig({
  displayName: "badge__BadgeRoot",
  componentId: "sc-aafp7i-4"
})(["position:relative;display:inline-block;color:unset;line-height:1;"]);
function stringifyCount(count) {
  let overflowCount = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  if (!isNumber(count)) {
    return "";
  }
  if (count > overflowCount) {
    return `${overflowCount}+`;
  }
  return String(count);
}
export const Badge = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    count,
    overflowCount = 99,
    showZero = false,
    dot = false,
    size = "default",
    status,
    color,
    title,
    text,
    offset,
    style,
    children,
    ...rest
  } = _ref;
  const numberCount = stringifyCount(count, overflowCount);
  const isZero = numberCount === "0";
  const isDot = dot && !isZero;
  const hasStatus = !!status || !!color;
  const displayCount = isDot ? "" : numberCount;
  const isEmpty = isNullish(displayCount) || isEmptyString(displayCount);
  const isHidden = (isEmpty || isZero && !showZero) && !isDot;
  const countRef = useRef(count);
  const displayCountRef = useRef(displayCount);
  const isDotRef = useRef(isDot);
  if (!isHidden) {
    countRef.current = count;
    displayCountRef.current = displayCount;
    isDotRef.current = isDot;
  }
  const getNumberTitle = () => {
    if (title) {
      return title;
    }
    return isNumber(countRef.current) ? String(countRef.current) : undefined;
  };
  if (!children && hasStatus) {
    const isPresetColor = !!color && presetColors.includes(color);
    const bg = isPresetColor ? `${color}.base` : color;
    return /*#__PURE__*/_jsxs(BadgeStatus, {
      ...rest,
      children: [/*#__PURE__*/_jsx(StatusDot, {
        status: status,
        bg: bg
      }), text ? /*#__PURE__*/_jsx(StatusText, {
        children: text
      }) : null]
    });
  }
  return /*#__PURE__*/_jsxs(BadgeRoot, {
    ref: ref,
    ...rest,
    children: [children, /*#__PURE__*/_jsx(Motion, {
      visible: !isHidden,
      motionName: children ? "ant-badge-zoom" : "",
      motionAppear: false,
      motionDeadline: 1000,
      children: _ref2 => {
        let {
          ref,
          className: motionClassName
        } = _ref2;
        const numberTitle = getNumberTitle();
        const offsetStyle = offset ? {
          right: -offset[0],
          top: offset[1]
        } : {};
        return /*#__PURE__*/_jsx(BadgeCount, {
          ref: ref,
          className: motionClassName,
          title: numberTitle,
          style: offsetStyle,
          "data-size": size,
          "data-dot": isDotRef.current,
          "data-wrapper": !!children,
          children: displayCountRef.current
        });
      }
    })]
  });
});
export default Badge;